import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'
import type { Schultyp } from '../../schulen/schultyp'

export interface Project {
  project_id: number
  project_code: string
  project_name: string
  projektleiter_person_id: number | null
  primary_location_id: number | null
  project_status: number
  description: string | null
  start_date: string | null
  end_date: string | null
  is_active: boolean
  audit_created_at: string
  audit_modified_at: string | null
  primary_location_name: string | null
  primary_location_code: string | null
  projektleiter_name: string | null
  projektleiter_email: string | null
  location_count: number
  staff_count: number
}

export interface ProjectLocation {
  project_location_id: number
  location_id: number
  relation_type: number
  delivery_source_location_id: number | null
  effective_from: string
  effective_to: string | null
  sort_order: number
  is_active: boolean
  notes: string | null
  location_code: string
  location_name: string
  location_type: number
  school_category: Schultyp | null
  city: string | null
  delivery_source_name: string | null
}

export interface ProjectAssignment {
  project_assignment_id: number
  project_id: number
  person_main_id: number
  assignment_role: number
  primary_location_id: number | null
  allocation_pct: number
  effective_from: string
  effective_to: string | null
  is_ferienplan: boolean
  is_active: boolean
  comment: string | null
  audit_created_at: string
  audit_modified_at: string | null
  person_name: string
  person_email: string | null
  person_title: string | null
  primary_location_name: string | null
  project_name: string
  project_code: string
}

export interface ProjectDetail extends Project {
  locations: ProjectLocation[]
  staff: ProjectAssignment[]
}

export interface ProjectInput {
  projectCode: string
  projectName: string
  projektleiterPersonId?: number | null
  primaryLocationId?: number | null
  projectStatus?: number
  description?: string | null
  startDate?: string | null
  endDate?: string | null
  isActive?: boolean
}

export interface ProjectLocationInput {
  locationId: number
  relationType?: number
  deliverySourceLocationId?: number | null
  effectiveFrom: string
  effectiveTo?: string | null
  sortOrder?: number
  notes?: string | null
}

export interface ProjectAssignmentInput {
  personMainId: number
  assignmentRole: number | string
  primaryLocationId?: number | null
  allocationPct?: number
  effectiveFrom: string
  effectiveTo?: string | null
  isFerienplan?: boolean
  comment?: string | null
}

export const PROJECT_STATUS_LABELS: Record<number, string> = { 1: 'Aktiv', 2: 'Planung', 3: 'Abgeschlossen' }
export const ASSIGNMENT_ROLE_LABELS: Record<number, string> = { 1: 'Projektleiter', 2: 'Standortleiter', 3: 'Stellvertretung', 4: 'Mitarbeiter', 5: 'Springer' }
export const RELATION_TYPE_LABELS: Record<number, string> = { 1: 'Primär', 2: 'Sekundär', 3: 'Lieferziel', 4: 'Nur Ferien' }

// ── Project hooks ────────────────────────────────────────────────────────────

export function useProjekte(filters?: { status?: number; active?: boolean; projektleiterId?: number }) {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', String(filters.status))
  if (filters?.active !== undefined) params.set('active', String(filters.active))
  if (filters?.projektleiterId) params.set('projektleiterId', String(filters.projektleiterId))
  const qs = params.toString()
  return useQuery({
    queryKey: ['projekte', filters],
    queryFn: () => api.get<Project[]>(`/api/projekte${qs ? `?${qs}` : ''}`),
    staleTime: 5 * 60_000,
  })
}

/**
 * Flat per-school → project mapping (location_id ↔ project + projektleiter).
 * One row per active school × active project. Schools without an active
 * project don't appear — callers should treat absence as "kein Projekt".
 */
export interface ProjektZuordnung {
  school_id:               number
  project_id:              number
  project_code:            string
  project_name:            string
  projektleiter_person_id: number | null
  projektleiter_name:      string | null
  relation_type:           number
  sort_order:              number
}

export function useProjektZuordnungen() {
  return useQuery({
    queryKey: ['projekt-zuordnungen'],
    queryFn:  () => api.get<ProjektZuordnung[]>('/api/projekt-zuordnungen'),
    staleTime: 5 * 60_000,
  })
}

export function useProjekt(id: number | null) {
  return useQuery({
    queryKey: ['projekte', id],
    queryFn: () => api.get<ProjectDetail>(`/api/projekte/${id}`),
    enabled: id != null,
    staleTime: 5 * 60_000,
  })
}

export function useCreateProjekt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ProjectInput) => api.post<Project>('/api/projekte', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projekte'] }),
  })
}

export function useUpdateProjekt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ProjectInput }) =>
      api.put<Project>(`/api/projekte/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projekte'] }),
  })
}

export function useDeleteProjekt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<{ deleted: boolean }>(`/api/projekte/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projekte'] }),
  })
}

// ── ProjectLocation hooks ────────────────────────────────────────────────────

export function useAddProjectLocation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: number; data: ProjectLocationInput }) =>
      api.post<ProjectLocation>(`/api/projekte/${projectId}/standorte`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projekte'] }),
  })
}

export function useRemoveProjectLocation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, id }: { projectId: number; id: number }) =>
      api.delete<{ deleted: boolean }>(`/api/projekte/${projectId}/standorte/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projekte'] }),
  })
}

// ── ProjectAssignment hooks ──────────────────────────────────────────────────

export function useProjectAssignments(projectId: number | null, filters?: { current?: boolean; role?: number }) {
  const params = new URLSearchParams()
  if (filters?.current) params.set('current', 'true')
  if (filters?.role) params.set('role', String(filters.role))
  const qs = params.toString()
  return useQuery({
    queryKey: ['projekt-assignments', projectId, filters],
    queryFn: () => api.get<ProjectAssignment[]>(`/api/projekte/${projectId}/assignments${qs ? `?${qs}` : ''}`),
    enabled: projectId != null,
    staleTime: 5 * 60_000,
  })
}

export function usePersonAssignments(personId: number | null, current = true) {
  return useQuery({
    queryKey: ['person-assignments', personId, current],
    queryFn: () => api.get<ProjectAssignment[]>(`/api/person-assignments?personId=${personId}${current ? '&current=true' : ''}`),
    enabled: personId != null,
    staleTime: 5 * 60_000,
  })
}

export function useCreateAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: number; data: ProjectAssignmentInput }) =>
      api.post<ProjectAssignment>(`/api/projekte/${projectId}/assignments`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projekte'] })
      qc.invalidateQueries({ queryKey: ['projekt-assignments'] })
    },
  })
}

export function useUpdateAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, id, data }: { projectId: number; id: number; data: Partial<ProjectAssignmentInput> }) =>
      api.put<ProjectAssignment>(`/api/projekte/${projectId}/assignments/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projekte'] })
      qc.invalidateQueries({ queryKey: ['projekt-assignments'] })
    },
  })
}

export function useDeleteAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, id }: { projectId: number; id: number }) =>
      api.delete<{ deleted: boolean }>(`/api/projekte/${projectId}/assignments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projekte'] })
      qc.invalidateQueries({ queryKey: ['projekt-assignments'] })
    },
  })
}
